import React, { useState } from 'react';
import { Button, Card, Col, Container, Row, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNull, isUndefined, map, startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLock, faPencil } from '@fortawesome/free-solid-svg-icons';
import { selectIsCustomer, selectIsFrontline, selectIsInsurer, selectIsCompany, selectIsProcessing } from '@components/Auth';
import { Loading, CopyLinkButton, LoadingError, RowItem, ConfirmModal } from '@components/Base';
import { dUserName } from '@models/User';
import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { InsurancePolicyUserCardBody } from '@components/Feature/InsurancePolicyUser';
import { QuotePremiumCardBody } from '@components/Feature/Quote';
import { IngestionCardBody } from '@components/Feature/Ingestion';
import { DocumentCardBody } from '@components/Feature/Document';
import { CollaborationCardBody } from '@components/Feature/Collaboration';
import { formatDate } from '@utils/datetime';
import { IngestionTypes, IngestableTypes } from '@models/Ingestion';
import { DocumentTypes } from '@models/Document';
import { CollaboratableTypes } from '@models/Collaboration';
import { InsurancePolicyBadge, InsurancePolicyDescriptionAccordion } from '@components/Feature/InsurancePolicy';
import { InsurancePolicyStatuses } from '@models/InsurancePolicy';
import { formatPercentage } from '@utils/currency';
import { PolicyCommissionInvoiceCardBody } from '@components/Feature/PolicyCommissionInvoice';

import { useInsurancePolicyFetch } from '../hook';
import { routeInsurancePolicyBuyEditPage, routeInsurancePolicyBuyPayPage } from '../route';
import {
  insurancePolicyLock,
  InsurancePolicyLockPayload,
  insurancePolicyPayByCash,
  InsurancePolicyPayByCashPayload,
} from '../action';

const ROW_ITEM_PROPS = {
  className: 'mb-2',
};

const InsurancePolicyBuyReviewPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isFrontline = useSelector(selectIsFrontline);
  const isCustomer = useSelector(selectIsCustomer);
  const isInsurer = useSelector(selectIsInsurer);
  const isCompany = useSelector(selectIsCompany);
  const isProcessing = useSelector(selectIsProcessing);
  const policy = useInsurancePolicyFetch();
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showPayByCashDialog, setShowPayByCashDialog] = useState(false);

  if (isUndefined(policy)) return <Loading />;
  if (isNull(policy)) return <LoadingError />;

  const {
    id,
    status,
    policyNumber,
    effectiveDate,
    expirationDate,
    frontline,
    descriptionHtml,
    company,
    insuranceType,
    policyType,
    user,
    insurerTeam,
    brokeragePercentage,
    canLock,
  } = policy;

  const isPending = status === InsurancePolicyStatuses.Pending;
  const isLocked = status === InsurancePolicyStatuses.Locked;
  const isActive = status === InsurancePolicyStatuses.Active;
  const shouldEdit = isFrontline && (isPending || isLocked || isActive);
  const shouldLock = isFrontline && isPending;
  const shouldPay = isCustomer && isLocked && policyType === PolicyTypes.Personal;
  const shouldPayByCash = isFrontline && isLocked && policyType === PolicyTypes.Commercial;
  const hidePremium = policyType === PolicyTypes.Commercial && isCustomer;
  const canClaim = isActive && !!policyNumber;

  return (
    <>
      <Container className="my-3">
        <Row className="gy-5">
          <Col lg={8}>
            <Card>
              <Card.Header className="d-flex flex-row justify-content-between align-items-center">
                {t('common:summary')}
                <CopyLinkButton />
              </Card.Header>
              <Card.Body>
                <Card.Title>{t('common:policyDetails')}</Card.Title>
                <RowItem {...ROW_ITEM_PROPS} label={t('common:policyNumber')} value={policyNumber} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:theInsuredPolicyholder')} value={policyType === PolicyTypes.Commercial ? company?.name : user?.name} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:periodOfInsurance')} value={`${formatDate(effectiveDate)} - ${formatDate(expirationDate)}`} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:insuranceType')} value={tInsuranceTypeEnum(insuranceType)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:policyType')} value={tPolicyType(policyType)} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:status')} value={<InsurancePolicyBadge status={status} />} />
                <RowItem {...ROW_ITEM_PROPS} label={t('common:frontlineUser')} value={dUserName(frontline)} />

                {(isFrontline || isInsurer || isProcessing) && (
                  <>
                    <RowItem {...ROW_ITEM_PROPS} label="Insurer Team" value={insurerTeam?.name} />
                    <RowItem {...ROW_ITEM_PROPS} label="Brokerage" value={formatPercentage(brokeragePercentage)} />
                  </>
                )}
              </Card.Body>

              {(isFrontline || isInsurer || isCompany || isProcessing) && (
                <Card.Body>
                  <InsurancePolicyDescriptionAccordion descriptionHtml={descriptionHtml} />
                </Card.Body>
              )}

              {(isFrontline || isInsurer || isCompany || isProcessing) && (
                <DocumentCardBody
                  documentableType='InsurancePolicy'
                  documentableId={policy.id}
                  documentType={DocumentTypes.PolicyInsurer}
                  canCreate={isFrontline || isInsurer || isCompany}
                />
              )}

              {(isFrontline || isInsurer || isCompany || isProcessing) && (
                <DocumentCardBody
                  documentableType='InsurancePolicy'
                  documentableId={policy.id}
                  documentType={DocumentTypes.PolicyCustomer}
                  canCreate={isFrontline || isInsurer || isCompany}
                />
              )}

              {(isFrontline) && (
                <IngestionCardBody
                  ingestableType={IngestableTypes.InsurancePolicy}
                  ingestableId={policy.id}
                  ingestionTypes={[IngestionTypes.InsurancePolicyUserAdd]}
                  canCreate={isFrontline || isInsurer || isCompany}
                />
              )}

              <InsurancePolicyUserCardBody
                {...policy}
                canCreate={isFrontline || isInsurer || isCompany}
                canClaim={canClaim}
              />

              {(isFrontline || isProcessing) && (
                <CollaborationCardBody
                  collaboratableType={CollaboratableTypes.InsurancePolicy}
                  collaboratableId={policy.id}
                  canCreate={isProcessing}
                  canDelete={isProcessing}
                  canEdit={isProcessing}
                />
              )}

              {(isFrontline || isProcessing) && (
                <PolicyCommissionInvoiceCardBody
                  id={policy.id}
                  canCreate={isProcessing}
                />
              )}
            </Card>
          </Col>

          <Col lg>
            <Card className="mb-4">
              <QuotePremiumCardBody {...policy} planName={map(policy.benefitTemplates, 'name').join(', ')} hidePremium={hidePremium} />

              <Card.Body>
                <Stack direction="vertical" gap={2}>
                  {shouldEdit && (
                    <Button as={Link} to={routeInsurancePolicyBuyEditPage({ insurancePolicyId: id })} variant="outline-primary">
                      <FontAwesomeIcon icon={faPencil} className="me-2" />
                      {t('common:edit')}
                    </Button>
                  )}

                  {shouldLock && (
                    <Button onClick={() => setShowLockDialog(true)} variant="warning" disabled={!canLock}>
                      <FontAwesomeIcon icon={faLock} className="me-2" />
                      {t('common:confirm')}
                    </Button>
                  )}

                  {shouldPay && (
                    <Button as={Link} to={routeInsurancePolicyBuyPayPage({ insurancePolicyId: id })}>
                      <span className="me-2">{startCase(t('common:payNow'))}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  )}

                  {shouldPayByCash && (
                    <Button onClick={() => setShowPayByCashDialog(true)}>
                      <span className="me-2">{startCase(t('common:payByCash'))}</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  )}
                </Stack>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {shouldLock && (
        <ConfirmModal
          show={showLockDialog}
          onHide={() => setShowLockDialog(false)}
          onConfirm={() => {
            const payload: InsurancePolicyLockPayload = { id };
            dispatch(insurancePolicyLock(payload));
          }}
        />
      )}

      {shouldPayByCash && (
        <ConfirmModal
          show={showPayByCashDialog}
          onHide={() => setShowPayByCashDialog(false)}
          onConfirm={() => {
            const payload: InsurancePolicyPayByCashPayload = { id };
            dispatch(insurancePolicyPayByCash(payload));
          }}
        />
      )}
    </>
  );
};

export { InsurancePolicyBuyReviewPage };
